// Helpers to extract informative descriptions from tool calls and results

// Helper function to create enhanced search query HTML with better UX
const createQueryHighlightHTML = (query, queryWords) => {
  const escapeHtml = (str) => {
    const escapeMap = { '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&#39;', '"': '&quot;' }
    return str.replace(/[<>&'"]/g, (char) => escapeMap[char])
  }

  const escapedQueryWords = escapeHtml(queryWords)

  // Escape the query for safe use in HTML attribute
  const tooltipText = `Full search query: "${query}"`
  const escapedTooltipText = escapeHtml(tooltipText)

  return `<span class="search-query-highlight" data-tooltip="${escapedTooltipText}" data-query-words="${escapedQueryWords}">${escapedQueryWords}...</span>`
}

export const extractDomainInfo = (toolArgs, toolResult = null) => {
  const domain = toolArgs?.domain
  if (!domain) return 'Domain analysis'
  if (toolResult) {
    try {
      const result = JSON.parse(toolResult)
      if (result.whois_data) {
        const creation = new Date(result.whois_data.creation_date).getFullYear()
        const registrar = result.whois_data.registrar?.split(',')[0] || 'Unknown registrar'
        return `${domain} • Registered ${creation} • ${registrar}`
      }
    } catch {}
  }
  return `${domain} • Analyzing domain registration...`
}

export const extractSenderInfo = (toolArgs, toolResult = null) => {
  if (toolResult) {
    try {
      const result = JSON.parse(toolResult)
      if (result.senderRelationCard?.sender_email) {
        return `${result.senderRelationCard.sender_email} • Reputation analysis completed`
      }
    } catch {}
  }
  return 'Sender reputation analysis...'
}

export const extractEmailBodyInfo = (toolArgs, toolResult = null) => {
  if (toolResult) {
    try {
      const result = JSON.parse(toolResult)
      if (result.email_body) {
        const wordCount = result.email_body.split(' ').length
        return `Email content • ${wordCount} words analyzed`
      }
    } catch {}
  }
  return 'Email content analysis...'
}

export const extractSenderRelations = () => 'Sender relations extraction...'

export const extractScanResultsInfo = (toolArgs, toolResult = null) => {
  if (toolResult) {
    try {
      const result = JSON.parse(toolResult)
      if (result.scan_results && Array.isArray(result.scan_results)) {
        const count = result.scan_results.length
        return `Security scan • ${count} results analyzed`
      }
    } catch {}
  }
  return 'Security scan analysis...'
}

export const extractTasks = (toolArgs, toolResult = null) => {
  if (toolResult) {
    try {
      const result = JSON.parse(toolResult)
      if (result.success) return `Created Plan tasks`
      return `Failed to create plan tasks`
    } catch {}
  }
  return 'Creating a plan...'
}

export const extractWebsearchInfo = (toolArgs, toolResult = null) => {
  const query = toolArgs?.query || toolArgs?.question
  const queryWords = query ? query.split(' ').slice(0, 3).join(' ') : 'query'
  if (toolResult !== null) {
    try {
      const parsed = JSON.parse(toolResult)
      const results = Array.isArray(parsed) ? parsed : parsed.results || []
      const domainsMap = new Map()
      for (const item of results) {
        if (item?.url) {
          try {
            const domain = new URL(item.url).hostname
            if (!domainsMap.has(domain) && item.favicon) {
              domainsMap.set(domain, item.favicon)
            }
          } catch {
            // Ignore if failed to parse domain hostname
          }
        }
      }
      const uniqueDomainCount = domainsMap.size
      return {
        type: 'structured',
        summary: `Searched "${createQueryHighlightHTML(query, queryWords)}" • ${
          results.length
        } results from ${uniqueDomainCount} domains`,
        domains: Array.from(domainsMap.entries()).map(([domain, favicon]) => ({ domain, favicon })),
        searchDone: true,
      }
    } catch (error) {
      // Ignore in case we failed to parse the result
    }
  }
  return `Searching "${createQueryHighlightHTML(query, queryWords)}"`
}

export const extractUrlsInfo = (toolArgs, toolResult = null) => {
  const urlOrUrls = toolArgs?.urls
  const firstUrl = Array.isArray(urlOrUrls) ? urlOrUrls[0] : urlOrUrls
  const queryWords = firstUrl ? firstUrl.split(' ').slice(0, 2).join(' ') : 'query'
  if (toolResult !== null) {
    try {
      const parsed = JSON.parse(toolResult)
      const results = parsed.results || []
      const failedResults = parsed.failed_results || []
      const domainsMap = new Map()

      // Process successful results
      for (const item of results) {
        if (item?.url) {
          try {
            const domain = new URL(item.url).hostname
            if (!domainsMap.has(domain)) {
              domainsMap.set(domain, item.favicon || '')
            }
          } catch {
            // Ignore if failed to parse domain hostname
          }
        }
      }

      // Process failed results
      for (const item of failedResults) {
        if (item?.url) {
          try {
            const domain = new URL(item.url).hostname
            if (!domainsMap.has(domain)) {
              domainsMap.set(domain, '') // No favicon for failed results
            }
          } catch {
            // Ignore if failed to parse domain hostname
          }
        }
      }

      const totalResults = results.length
      const totalFailed = failedResults.length
      const uniqueDomainCount = domainsMap.size
      const successText =
        totalFailed > 0
          ? `${totalResults} extracted, ${totalFailed} failed`
          : `${totalResults} extracted`

      return {
        type: 'structured',
        summary: `Extracted "${queryWords}..." • ${successText} from ${uniqueDomainCount} domains`,
        domains: Array.from(domainsMap.entries()).map(([domain, favicon]) => ({ domain, favicon })),
        searchDone: true,
      }
    } catch (error) {
      // Ignore in case we failed to parse the result
    }
  }
  return `Extracting "${queryWords}..."`
}

export const extractTaskStatusUpdate = (toolArgs, toolResult = null) => {
  const status = toolArgs?.status
  const taskId = toolArgs?.task_id
  if (toolResult) {
    try {
      const result = JSON.parse(toolResult)
      if (result.success) return `Task ${taskId} → ${status}`
      return `Task update failed: ${result.error || 'Unknown error'}`
    } catch {
      return `Task ${taskId} → ${status}`
    }
  }
  return `Updating task ${taskId}...`
}

export const extractDnsTool = (toolArgs, toolResult = null) => {
  const domain = toolArgs?.domain
  const recordTypes = Array.isArray(toolArgs?.record_types)
    ? toolArgs.record_types.join(', ')
    : toolArgs?.record_types
  return `Making a DNS lookup for ${domain} • ${recordTypes}`
}

const countryCodeToFlag = (countryCode) => {
  if (typeof countryCode !== 'string') return ''
  let code = countryCode.trim().toUpperCase()

  // Common alias
  if (code === 'UK') code = 'GB'

  // Validate strictly A–Z A–Z
  if (!/^[A-Z]{2}$/.test(code)) return ''

  const codePoints = [...code].map((c) => 127397 + c.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

const parseCountryCode = (toolResult, enrichedIps) => {
  if (!toolResult) return

  const lines = toolResult.split('\n')

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]?.trim()
    if (!line || line.startsWith(';;')) continue

    // Expect lines like: "192.64.119.42\tGeoIP\tUnited States|US, Unknown"
    const parts = line.split('\t')
    if (parts.length < 3) continue

    const ip = parts[0].trim()
    const locationInfo = parts[2].trim() // e.g., "United States|US, Unknown"

    // Extract "countryPart" and "city"
    const [countryPartRaw, ...cityParts] = locationInfo.split(',')
    const countryPart = (countryPartRaw || '').trim()
    const cityRaw = cityParts.join(',').trim() // handles cities with commas
    const hasCity = cityRaw && cityRaw !== 'Unknown'

    let country = countryPart
    let countryCode = null

    // If "Country|CC" form is present
    if (countryPart.includes('|')) {
      const [countryName, code] = countryPart.split('|')
      country = (countryName || '').trim()
      countryCode = (code || '').trim()
    }

    const idx = enrichedIps.findIndex((item) => item.domain === ip)
    if (idx !== -1) {
      // let flag = countryCode ? countryCodeToFlag(countryCode) : ''
      // const ip = enrichedIps[idx].domain

      enrichedIps[idx] = {
        ...enrichedIps[idx],
        country,
        flag: countryCode ? countryCodeToFlag(countryCode) : '',
      }

      // enrichedIps[idx].domain = `${flag} ${ip}`
      // enrichedIps[idx].country = country
    }
  }
}

export const extractIpsFromToolArgs = (toolArgs, message, toolResult = null) => {
  const ips = Array.isArray(toolArgs?.ips) ? toolArgs.ips : [toolArgs?.ips].filter(Boolean)

  const enrichedIps = ips.map((ip) => ({
    domain: String(ip),
    favicon: null,
  }))

  if (toolResult) {
    parseCountryCode(toolResult, enrichedIps)
  }

  return {
    type: 'structured',
    summary: `${message}`,
    domains: enrichedIps,
    searchDone: true,
  }
}

// Build source data object for known tools
export const generateSourceData = (toolCallId, toolName, toolArgs, toolResult = null) => {
  const sourceConfig = {
    domain_whois: {
      title: 'Domain Intelligence',
      description: () => extractDomainInfo(toolArgs, toolResult),
      icon: '🌐',
      type: 'WHOIS Data',
      key: 'domainIntelligence',
    },
    sender_relation_card: {
      title: 'Sender Analysis',
      description: () => extractSenderInfo(toolArgs, toolResult),
      icon: '👤',
      type: 'Reputation',
      key: 'senderRelationCard',
    },
    get_email_body: {
      title: 'Email Content',
      description: () => extractEmailBodyInfo(toolArgs, toolResult),
      icon: '📧',
      type: 'Content',
      key: 'emailBody',
    },
    sender_relation_card_by_id: {
      title: 'Sender Relations',
      description: () => extractSenderRelations(toolArgs, toolResult),
      icon: '📩',
      type: 'Sender',
      key: 'senderRelations',
    },
    get_scan_results: {
      title: 'Security Scan',
      description: () => extractScanResultsInfo(toolArgs, toolResult),
      icon: '🔍',
      type: 'Scan Results',
      key: 'scanResults',
    },
    incident_by_id: {
      title: 'Incident Data',
      description: () => 'Core incident information and metadata',
      icon: '📊',
      type: 'Report Data',
      key: 'incidentData',
    },
    get_email_screenshot: {
      title: 'Email Screenshot',
      description: () => 'Analyzed email screenshot',
      icon: '🖥️',
      type: 'Screenshot',
      key: 'emailScreenshot',
    },
    get_link_screenshot: {
      title: 'Link Screenshot',
      description: () => 'Analyzed link screenshot',
      icon: '🔗️',
      type: 'Screenshot',
      key: 'linkScreenshot',
    },
    get_incident_attachment: {
      title: 'Attachment',
      description: () => 'Analyzed attachment',
      icon: '🗃️️',
      type: 'Attachment',
      key: 'incidentAttachment',
    },
    grounded_web_search_tool: {
      title: 'Internet Search',
      description: () => extractWebsearchInfo(toolArgs, toolResult),
      icon: '🌐',
      type: 'Search Results',
      key: 'internetSearch',
    },
    tavily_search: {
      title: 'Internet Search',
      description: () => extractWebsearchInfo(toolArgs, toolResult),
      icon: '🌐',
      type: 'Search Results',
      key: 'internetSearch',
    },
    tavily_extract_webpages: {
      title: 'Webpage Extraction',
      description: () => extractUrlsInfo(toolArgs, toolResult),
      icon: '🌐',
      type: 'Webpage Extraction',
      key: 'tavilyExtractWebpages',
    },
    create_tasks: {
      title: 'Create tasks',
      description: () => extractTasks(toolArgs, toolResult),
      icon: '📋',
      type: 'Planning',
      key: 'createTasks',
    },
    transition_task_status: {
      title: 'Task Status Update',
      description: () => extractTaskStatusUpdate(toolArgs, toolResult),
      icon: '📋',
      type: 'Task Update',
      key: 'transitionTaskStatus',
    },
    dns_lookup: {
      title: 'DNS lookup',
      description: () => extractDnsTool(toolArgs, toolResult),
      icon: '⚙️',
      type: 'DNS',
      key: 'dnsLookup',
    },
    reverse_dns_lookup: {
      title: 'DNS reverse lookup',
      description: () => extractIpsFromToolArgs(toolArgs, 'Reverse DNS lookup'),
      icon: '⚙️',
      type: 'DNS',
      key: 'reverseDnsLookup',
    },
    geoip_lookup: {
      title: 'GEO IPs lookup',
      description: () => extractIpsFromToolArgs(toolArgs, 'Checking Geo Location', toolResult),
      icon: '🌍',
      type: 'GeoIP',
      key: 'geoipLookup',
    },
  }

  const config = sourceConfig[toolName]
  if (!config) return null

  // Execute the description function only for the matching tool
  const description = config.description()

  return {
    id: toolCallId,
    toolCallId,
    toolName,
    ...config,
    description,
    timestamp: new Date().toISOString(),
    status: toolResult ? 'completed' : 'loading',
  }
}
